import { TenantSelector } from '../../../components/tenant-selector/TenantSelector';
import { AutomationEditor } from './AutomationEditor';
import { AutomationStatusSummary } from './draft-status/AutomationStatusSummary';
import { DraftBanner } from './draft-status/DraftBanner';
import styles from './EditAutomationTemplate.module.css';
import { EditorSyncStatusProvider } from './EditorSyncStatusProvider';
import { ReorderWarningDialog } from './ReorderWarningDialog';
import { SegmentAdditionConfirmDialog } from './SegmentAdditionConfirmDialog';
import { SettingsControl } from './settings-controls';
import { TenantSelectionControl } from './tenant-selection-control/TenantSelectionControl';
import { TenantSelectionDialog } from './tenant-selection-control/TenantSelectionDialog';
import { useEditAutomationTemplateContent } from './useEditAutomationTemplateContent';

const { headerContent, syncButton, reorderNotification } = styles;

const AUTOMATION_DRAFT_BANNER_TITLE = 'This automation is a draft';
const AUTOMATION_DRAFT_BANNER_BODY =
  "It isn't synced to any tenant yet, so nobody receives it. Sync it to tenants when you're ready to go live.";

export function EditAutomationTemplateContent() {
  const {
    name,
    description,
    templateStatus,
    availabilitySummary,
    isDraft,
    isSyncing,
    templateId,
    tenantRowData,
    isLoadingTenants,
    hasReorderedOrAdded,
    undoReorder,
    tenantSelection,
    saveButtonLabel,
    saveDisabled,
    saveTitle,
    handleSaveClick,
    saveAfterConfirmingSegmentAdditions,
    segmentAdditions,
    closeSegmentAdditions,
    handleSave,
    showReorderWarning,
    setShowReorderWarning,
    tenantDialogOpen,
    openTenantDialog,
    closeTenantDialog,
  } = useEditAutomationTemplateContent();

  return (
    <EditorSyncStatusProvider isSyncing={isSyncing}>
      <header className={headerContent}>
        <hgroup>
          <h1>{name}</h1>
          <p>{description}</p>
          <AutomationStatusSummary status={templateStatus} summary={availabilitySummary} />
          {hasReorderedOrAdded && (
            <div className={reorderNotification}>
              <span>Automation steps have been added, deleted or re-ordered.</span>
              <button
                type="button"
                onClick={undoReorder}
                aria-label="Undo reorder"
                disabled={isSyncing}
                title={isSyncing ? 'Not available while sync is in progress' : undefined}>
                Undo?
              </button>
            </div>
          )}
        </hgroup>
        <aside>
          <TenantSelectionControl onOpen={openTenantDialog} />
          <SettingsControl />
          <button
            className={syncButton}
            type="button"
            onClick={handleSaveClick}
            disabled={saveDisabled}
            title={saveTitle}>
            {saveButtonLabel}
          </button>
        </aside>
      </header>

      {!isSyncing && isDraft && (
        <DraftBanner
          onSync={openTenantDialog}
          title={AUTOMATION_DRAFT_BANNER_TITLE}
          body={AUTOMATION_DRAFT_BANNER_BODY}
        />
      )}

      {isSyncing ? (
        <div className={styles.syncingBody}>
          <p className={styles.syncingMessage}>Cannot edit template while syncing</p>
          <TenantSelector
            selection={tenantSelection}
            onChange={() => {}}
            allTenantsSelectionDescription=""
            selectedTenantsSelectionDescription=""
            disabled
            tenantRowData={tenantRowData}
            listOnly
          />
        </div>
      ) : (
        <div className="mt-4">
          <AutomationEditor />
        </div>
      )}

      {tenantDialogOpen && (
        <TenantSelectionDialog
          templateId={templateId}
          onClose={closeTenantDialog}
          tenantRowData={tenantRowData}
          isLoading={isLoadingTenants}
        />
      )}

      {segmentAdditions && (
        <SegmentAdditionConfirmDialog
          conflicts={segmentAdditions}
          onClose={closeSegmentAdditions}
          onConfirm={handleSave}
        />
      )}

      <ReorderWarningDialog
        open={showReorderWarning}
        onClose={() => setShowReorderWarning(false)}
        onConfirm={saveAfterConfirmingSegmentAdditions}
      />
    </EditorSyncStatusProvider>
  );
}
